<script lang="ts">
import type { I3pService } from '@nuxtDatakodeUtils/services/i3p.service.js'
import { useCookie, useRuntimeConfig } from 'nuxt/app'
import { type PropType, defineComponent } from 'vue'
import EntityInitSection from '~/components/I3pComponent/entity/EntityInitSection.vue'
import type { Command } from '~/models/Command'

export default defineComponent({
    name: 'CommandEntityDownloadFile',
    extends: EntityInitSection,
    props: {
        i3pService: { type: Object as PropType<I3pService<unknown>>, required: true },
    },
    emits: ['updateData', 'save', 'updateMapPosition'],
    data() {
        const config = useRuntimeConfig()
        const access_token = useCookie<string>('access_token')
        return {
            config,
            access_token,
            loading: false,
            codeAsked: false,
            codeSent: false,
            downloaded: false,
            downLoadUrl: 'changeMe',
            downLoadText: 'changeMe',
            verificationCode: '',
        }
    },
    computed: {
        isAvailable(): boolean {
            return this.data?.isAvailable
        },
        isDownloaded(): boolean {
            return this.data?.status === 'downloaded' || this.downloaded
        },
        command(): Command {
            return this.i3pService.currentEntity as Command
        },
        uuid(): string {
            return this.data?.uuid || ''
        },
        canAskCode(): boolean {
            return this.data?.canAskCode
        },
        isFileExpired(): boolean {
            return this.data?.file?.is_expired || false
        },
        hasValidToken(): boolean {
            return this.data?.file?.has_valid_token || false
        },
    },
    watch: {
        uuid: {
            handler() {
                this.resedDefault()
            },
            deep: true,
            immediate: true,
        },
        hasValidToken: {
            handler(newHasValidToken: boolean) {
                this.codeAsked = newHasValidToken
            },
            deep: true,
            immediate: true,
        },
    },
    mounted() {
        const route = useRoute()
        this.verificationCode = route.query?.code ?? ''
        if (this.verificationCode !== '' || !this.data.canAskCode)
            this.codeAsked = true
    },
    methods: {
        resedDefault() {
            this.codeAsked = false
            this.codeSent = false
            this.downloaded = false
            this.downLoadUrl = 'changeMe'
            this.downLoadText = 'changeMe'
            this.verificationCode = ''
        },
        async askCode(): Promise<void> {
            this.loading = true
            try {
                const res = await this.command.askCode(this.uuid)
                if (res)
                    this.codeAsked = true
            }
            catch (e) {
                this.codeAsked = false
                console.warn(e)
            }
            finally {
                this.loading = false
            }
        },
        async downloadFile(): Promise<void> {
            this.loading = true
            try {
                const res = await this.command.downloadFile(this.uuid, this.verificationCode)

                const disposition = res.headers.get('content-disposition') || ''
                const filename
                    = disposition.match(/filename="?([^"]+)"?/)?.[1]
                        ?? `result-${this.uuid}.zip`

                const blob = res._data as Blob
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                document.body.appendChild(a)
                a.href = url
                a.download = filename
                a.click()
                window.URL.revokeObjectURL(url)
                a.remove()

                this.downloaded = true
                await this.i3pService.reloadCurrenEntity()
                await this.i3pService.refreshData()
            }
            finally {
                this.loading = false
            }
        },
    },
})
</script>

<template>
    <FormSection v-if="isAvailable || isDownloaded || isFileExpired" title="Téléchargement" with-borders-color="primary">
        <template v-if="isDownloaded">
            {{ $t('majic.commands.fileDownloaded.index') }}
        </template>
        <template v-else-if="isFileExpired">
            {{ $t('majic.commands.fileDownloaded.isExpired') }}
        </template>
        <template v-else-if="!isDownloaded">
            <template v-if="!codeAsked">
                <p class="pb-2 text-body-2">
                    Le fichier est prêt à être téléchargé, il sera disponible jusqu'au <strong>{{
                        new Date(data.file?.file_expires_at).toLocaleDateString('fr-FR', {
                            year: "numeric",
                            month: "numeric",
                            day: "numeric",
                        })
                    }}</strong>. Un code de sécurité va vous être envoyé par courriel.
                </p>
                <v-btn
                    color="primary"
                    :text="$t(`${translationName}.askCode`)"
                    :loading
                    append-icon="mdi-email-check"
                    @click="askCode()"
                />
            </template>
            <template v-else>
                <p class="text-body-2">
                    Le téléchargement est unique. Une fois démarré, il ne pourra plus être relancé.
                </p>
                <span>
                    <strong>{{ $t(`${translationName}.codeInput`) }}</strong> <span class="font-italic text-caption v-label">(obligatoire)</span>
                </span>

                <div class="text-center">
                    <v-otp-input
                        v-model="verificationCode"
                        lenght="6"
                        variant="outlined"
                        :loading="loading"
                        @finish="downloadFile"
                    />
                    <v-btn
                        :disabled="verificationCode.length < 6"
                        color="primary"
                        append-icon="mdi-download"
                        :loading="loading"
                        @click="downloadFile"
                    >
                        {{ $t(`${translationName}.download`) }}
                    </v-btn>
                </div>
                <p class="pa-2 d-flex align-center">
                    Besoin d'un nouveau code ?
                    <v-btn
                        variant="text"
                        append-icon="mdi-email-check"
                        color="primary"
                        @click="askCode()"
                    >
                        Renvoyer un code
                    </v-btn>
                </p>
            </template>
        </template>
    </FormSection>
</template>
